import { useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { calculateRange } from 'components/Dropdown/DateTimeRangeFilter/useDateTimeRangePicker';
import { exportDataReconciliation, fetchDataReconciliation } from 'service/service';
import { getResponseTable, getTableColumns, Program, ProgramTable } from '../utils';

type SortingInfo = { sortBy: string; sortDir: string };
type DateRangePayload = {
  dateTimeRangeType: string;
  timeZone: string;
  startTime?: string;
  endTime?: string;
};
type DataReconciliationRequestParams = {
  key: Program[];
  sortingInfo?: SortingInfo;
  dateRange: DateRangePayload;
};
type DataReconciliationExportParams = DataReconciliationRequestParams & {
  formatType: string;
  exportColumns: { key: string; label: string }[];
};

const RECONCILIATION_TIMEZONE = 'America/Chicago';
const RECONCILIATION_TIMEZONE_LABEL = 'CST';
const DATE_TIME_FORMAT = 'DD-MM-YYYY HH:mm:ss';

const getDateRangePayload = (type: string, start?: Date, end?: Date): DateRangePayload => {
  const range = type === 'custom'
    ? { from: start, to: end }
    : calculateRange(type, RECONCILIATION_TIMEZONE);

  return {
    dateTimeRangeType: type,
    timeZone: RECONCILIATION_TIMEZONE_LABEL,
    ...(range?.from && { startTime: moment(range.from).tz(RECONCILIATION_TIMEZONE).format(DATE_TIME_FORMAT) }),
    ...(range?.to && { endTime: moment(range.to).tz(RECONCILIATION_TIMEZONE).format(DATE_TIME_FORMAT) }),
  };
};

const getStoredDateRangePayload = (program: Program): DateRangePayload => {
  const prefix = program === 'Master' ? 'dataReconMaster' : 'dataReconTransactional';
  const defaultRange = program === 'Master' ? 'today' : '7d';
  const type = sessionStorage.getItem(`${prefix}DateRangeType`) || defaultRange;
  if (type === 'custom') {
    const startDate = sessionStorage.getItem(`${prefix}StartDate`);
    const endDate = sessionStorage.getItem(`${prefix}EndDate`);
    return getDateRangePayload(type, startDate ? new Date(startDate) : undefined, endDate ? new Date(endDate) : undefined);
  }
  return getDateRangePayload(type);
};

export const useDataReconciliation = (activeProgram: Program) => {
  const [masterTable, setMasterTable] = useState<ProgramTable>({ rows: [], total: 0 });
  const [transactionalTable, setTransactionalTable] = useState<ProgramTable>({ rows: [], total: 0 });
  const [loadingTables, setLoadingTables] = useState<Record<Program, boolean>>({
    Master: true,
    Transactional: true,
  });
  const [sortingInfo, setSortingInfo] = useState<Record<Program, SortingInfo>>({
    Master: { sortBy: '', sortDir: '' },
    Transactional: { sortBy: '', sortDir: '' },
  });
  const [isExporting, setIsExporting] = useState(false);
  const [dateRangeRevision, setDateRangeRevision] = useState(0);
  const dateRangePayload = useMemo(
    () => getStoredDateRangePayload(activeProgram),
    [activeProgram, dateRangeRevision]
  );

  useEffect(() => {
    const syncDateRange = () => setDateRangeRevision((revision) => revision + 1);
    window.addEventListener('dataReconSessionStorageUpdated', syncDateRange);
    return () => window.removeEventListener('dataReconSessionStorageUpdated', syncDateRange);
  }, []);

  useEffect(() => {
    let isMounted = true;
    setLoadingTables((current) => ({ ...current, [activeProgram]: true }));
    const params: DataReconciliationRequestParams = {
      key: [activeProgram],
      sortingInfo: sortingInfo[activeProgram],
      dateRange: dateRangePayload,
    };
    fetchDataReconciliation(params)
      .then((response) => {
        if (!isMounted) return;
        const table = getResponseTable(response, activeProgram);
        if (activeProgram === 'Master') setMasterTable(table);
        else setTransactionalTable(table);
      })
      .catch((error) => console.error('Error loading Data Reconciliation tables:', error))
      .finally(() => {
        if (isMounted) setLoadingTables((current) => ({ ...current, [activeProgram]: false }));
      });

    return () => { isMounted = false; };
  }, [activeProgram, sortingInfo[activeProgram], dateRangePayload]);

  const tableViews = useMemo(() => [
    { program: 'Transactional' as const, table: transactionalTable },
    { program: 'Master' as const, table: masterTable },
  ].map(({ program, table }) => {
    return {
      program,
      isLoading: loadingTables[program],
      sortedRows: table.rows,
      rows: table.rows,
      programColumns: getTableColumns(table.rows, program),
      total: table.total,
      sortingInfo: sortingInfo[program],
      onSorting: (nextSortingInfo: SortingInfo) => {
        setSortingInfo((current) => ({ ...current, [program]: nextSortingInfo }));
      },
    };
  }), [masterTable, transactionalTable, sortingInfo, loadingTables]);

  const handleExport = async (
    program: Program = 'Transactional',
    programColumns: any[] = [],
    preferredColumnNames: string[] = []
  ) => {
    if (isExporting) return;
    setIsExporting(true);
    const minDelay = new Promise((resolve) => setTimeout(resolve, 800));
    try {
      const selectedColumns = preferredColumnNames.length
        ? preferredColumnNames
            .map((columnName) => {
              const match = programColumns.find((column) => column.name === columnName);
              if (match) {
                return { key: match.key, label: match.name };
              }
              return { key: columnName, label: columnName };
            })
            .filter((column) => column && column.key)
        : (programColumns || []).map((column) => ({ key: column.key, label: column.name })).filter((column) => column && column.key);

      const exportPayload: DataReconciliationExportParams = {
        key: [program],
        sortingInfo: sortingInfo[program]?.sortBy ? sortingInfo[program] : { sortBy: '', sortDir: '' },
        dateRange: dateRangePayload,
        formatType: 'EXCEL',
        exportColumns: selectedColumns,
      };

      await Promise.all([
        exportDataReconciliation(exportPayload),
        minDelay,
      ]);
    } catch (error) {
      console.error('Error exporting Data Reconciliation table:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return {
    tableViews,
    sortingInfo,
    setSortingInfo,
    handleExport,
    isExporting,
    dateRangePayload,
  };
};